/**
 * Service-worker update plumbing.
 *
 * `registerType: "prompt"` means a new build installs itself but waits for permission before
 * taking over. That is the right behaviour — swapping the app out from under someone mid-session
 * is worse than a day-old build — but it only works if something actually asks. Nothing did, so
 * new versions sat waiting indefinitely and installed phones stayed on whatever they first got.
 */

export const UPDATE_READY = "kaya-update-ready";

let apply: ((reload?: boolean) => Promise<void>) | null = null;

export function setUpdater(fn: (reload?: boolean) => Promise<void>) {
  apply = fn;
}

export function announceUpdate() {
  window.dispatchEvent(new Event(UPDATE_READY));
}

/** Activates the waiting worker and reloads onto it. */
export async function applyUpdate() {
  if (!apply) {
    window.location.reload();
    return;
  }
  await apply(true);
}

/**
 * Whether a build is installed and waiting to take over.
 *
 * `waiting` is the state a new worker sits in once installed, and it persists across page loads.
 * That is the case the library's own callback misses.
 */
function hasWaitingBuild(reg: ServiceWorkerRegistration): boolean {
  return !!reg.waiting && !!navigator.serviceWorker.controller;
}

/**
 * Watches a registration for a build that is ready.
 *
 * vite-plugin-pwa only raises `onNeedRefresh` from an `updatefound` event fired **while the page
 * is open**. A worker that finished installing during a previous visit is already sitting in
 * `waiting` when the page loads, no event is ever raised for it, and the prompt never appears —
 * which is precisely what happens after a few deploys in a row. So check the state directly on
 * registration, and again whenever a worker transitions into it.
 *
 * An installed PWA also runs for days without a navigation, and the browser only checks for a new
 * worker on one, so polling is what makes an update arrive at all on a phone that is never closed.
 */
export function pollForUpdates(reg: ServiceWorkerRegistration | undefined, swapNow?: () => Promise<boolean>) {
  if (!reg) return;

  // The case the library misses: already waiting before this page existed. Nothing has been
  // started yet, so when the caller allows it the build takes over now rather than sit behind a
  // notice that may never be tapped — a phone stayed on a September build for ten days that way.
  // With a session open on this phone the notice stays the right answer.
  if (hasWaitingBuild(reg)) {
    if (swapNow) void swapNow().then((yes) => (yes ? applyUpdate() : announceUpdate()));
    else announceUpdate();
  }

  // And the case it handles, watched directly so it does not depend on the library's wiring.
  reg.addEventListener("updatefound", () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener("statechange", () => {
      if (sw.state === "installed" && navigator.serviceWorker.controller) announceUpdate();
    });
  });

  const check = () => {
    void reg
      .update()
      .then(() => {
        if (hasWaitingBuild(reg)) announceUpdate();
      })
      .catch(() => {
        /* offline, or the server is briefly down — try again next hour */
      });
  };

  window.setInterval(check, 60 * 60 * 1000);
  // and whenever the app comes back to the foreground, which is when a user would notice
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}

/** The commit the server is serving, from `build.txt` beside the bundle (vite.config.ts), or null
 *  offline, on an older server, or in dev, where the path falls through to index.html. */
export async function serverBuild(): Promise<string | null> {
  try {
    const r = await fetch("/build.txt", { cache: "no-store" });
    if (!r.ok) return null;
    const first = (await r.text()).trim().split(/\s+/)[0] || "";
    return /^[0-9a-f]{7,40}$/.test(first) ? first : null;
  } catch {
    return null;
  }
}

/**
 * A manual check, so there is always a way to pull an update rather than waiting to be offered one.
 *
 * "behind" is the case the worker cannot see: this phone runs an older build than the server and
 * nothing is waiting — a worker that failed to install, or a browser that never re-fetched sw.js.
 * The caller then `reinstall()`s.
 */
export async function checkNow(): Promise<"ready" | "current" | "behind" | "unsupported"> {
  if (!("serviceWorker" in navigator)) return "unsupported";
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return "unsupported";
  try {
    await reg.update();
  } catch {
    /* offline */
  }
  if (hasWaitingBuild(reg)) {
    announceUpdate();
    return "ready";
  }
  const server = await serverBuild();
  if (server && server !== __BUILD_COMMIT__) return "behind";
  return "current";
}

/** Drops the worker and reloads: the page comes back uncontrolled, fetches the current index.html
 *  and registers the current worker. Only after `checkNow()` said "behind", which means the server
 *  answered, so the phone is online and will not be left without an offline copy. */
export async function reinstall(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) await reg.unregister();
  window.location.reload();
}
